import { useEffect, useRef } from 'react'
import type { AppConfig } from '../types'
import type { Action, BookingState } from '../booking/state'
import { isTelemed } from '../booking/state'
import { formatDateLong, formatTime12 } from '../dates'
import { googleCalendarUrl } from '../calendar'
import { Icon } from '../icons'

/** 7.7 Confirmation. Shows the appointment summary + any profile/sub-profile message. */
export function Confirmation({
  config,
  state
}: {
  config: AppConfig
  state: BookingState
  dispatch?: React.Dispatch<Action>
}) {
  const slot = state.slot
  const e = slot?.entity
  const telemed = isTelemed(state.profile, state.subProfile)

  // Announce success: move focus to the confirmation heading on mount so
  // screen-reader users hear that the booking went through.
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true })
  }, [])
  const visitMessage = state.subProfile?.message || state.profile?.message
  const isProvider = e?.doctor_id != null

  // "Add to calendar" — a single Google event carrying the appointment details,
  // a real 30-minute timed event (§4).
  const calendarUrl =
    slot &&
    googleCalendarUrl({
      title: `${config.company.name} Appointment`,
      details: [
        `${state.profile?.name}${state.subProfile ? ` · ${state.subProfile.name}` : ''}`,
        e ? `${isProvider ? 'Provider' : 'Office'}: ${e.name}` : '',
        state.confirmationId ? `Reference: ${state.confirmationId}` : ''
      ]
        .filter(Boolean)
        .join('\n'),
      location: !telemed && e?.address ? `${e.address}${e.city ? `, ${e.city}` : ''} ${e.state ?? ''} ${e.zip ?? ''}`.trim() : undefined,
      date: slot.date,
      time: slot.time,
      durationMinutes: 30
    })

  return (
    <div className="confirm-card">
      <div className="confirm-check" aria-hidden>
        ✓
      </div>
      <h1 className="step-heading" style={{ textAlign: 'center' }} tabIndex={-1} ref={headingRef}>
        You’re booked!
      </h1>
      <p className="step-sub" style={{ textAlign: 'center' }}>
        A confirmation has been sent.
      </p>

      {slot && (
        <div style={{ textAlign: 'left', marginTop: 18 }}>
          <div className="summary-row">
            <span className="k">Service</span>
            <span className="v">
              {state.profile?.name}
              {state.subProfile ? ` · ${state.subProfile.name}` : ''}
            </span>
          </div>
          <div className="summary-row">
            <span className="k">When</span>
            <span className="v">
              {formatDateLong(slot.date)} · {formatTime12(slot.time)} CT
            </span>
          </div>
          {e && (
            <div className="summary-row">
              <span className="k">{isProvider ? 'Provider' : 'Office'}</span>
              <span className="v">{e.name}</span>
            </div>
          )}
          {!telemed && e?.address && (
            <div className="summary-row">
              <span className="k">Address</span>
              <span className="v">
                {e.address}
                {e.city ? `, ${e.city}` : ''} {e.state} {e.zip}
              </span>
            </div>
          )}
          {e?.phone && (
            <div className="summary-row">
              <span className="k">Phone</span>
              <span className="v">
                <a href={`tel:${e.phone.replace(/[^\d+]/g, '')}`}>{e.phone}</a>
              </span>
            </div>
          )}
        </div>
      )}

      {visitMessage && <div className="message-banner">{visitMessage}</div>}

      {/* New patients only — paperwork done ahead of time is the single biggest
          time saver at check-in. Links to company.registration_url when the
          brand configures one; otherwise it's still a useful prompt. */}
      {state.status === 'new' && (
        <div className="reg-note">
          <strong>Save time:</strong>{' '}
          {config.company.registration_url ? (
            <a href={config.company.registration_url} target="_blank" rel="noreferrer">
              Fill out your registration ahead of time
            </a>
          ) : (
            'Fill out your registration ahead of time.'
          )}
        </div>
      )}

      {calendarUrl && (
        <div className="btn-row" style={{ justifyContent: 'center' }}>
          <a className="btn btn-primary add-cal" href={calendarUrl} target="_blank" rel="noreferrer">
            <Icon value="calendar" size={18} /> Add to calendar
          </a>
        </div>
      )}
    </div>
  )
}
